// LayoutBlock ツリー → Preact 要素（render.mjs renderBlock の移植）。
// 安全上限（RISK B-5）: はみ出し・入れ子3段超・未知 component は描画スキップ（落ちない）。
import type { ComponentChildren } from "preact";
import { blockGridCss, styleCss, label, isVisible, enumChip, MAX_DEPTH } from "./model.mjs";
import { EditControl } from "./EditControl";
import { Chart } from "./Chart";
import type { RenderCtx } from "./context";
import type { LayoutBlock, Row } from "../types";

export function Block({
  block,
  ctx,
  row,
  depth,
  gridColumns,
}: {
  block: LayoutBlock;
  ctx: RenderCtx;
  row?: Row;
  depth: number;
  gridColumns: number;
}) {
  if (!isVisible(block.visibility, row ?? {}, ctx.evalCtx)) return null;

  const gridCss = blockGridCss(block.position, gridColumns);
  if (gridCss == null) return null; // 不正位置はスキップ

  const val = block.binding ? row?.[block.binding.columnId] : undefined;
  const col = block.binding ? ctx.table?.columns?.find((c) => c.id === block.binding!.columnId) : undefined;

  let inner: ComponentChildren = null;
  switch (block.component) {
    case "text":
      inner = label(block.content, ctx.locale);
      break;
    case "field":
      inner =
        ctx.mode === "edit" ? (
          <EditControl
            col={col}
            columnId={block.binding?.columnId}
            value={val}
            locale={ctx.locale}
            onInput={(key, v) => row && ctx.onInput?.(row, key, v)}
          />
        ) : (
          (() => {
            const chip = enumChip(col, val, ctx.tokens, ctx.locale);
            return chip ? (
              <span class="ss-chip" style={`background:${chip.bg};color:${chip.fg}`}>{chip.text}</span>
            ) : (
              <span class="fld">{String(val ?? "")}</span>
            );
          })()
        );
      break;
    case "chart":
      inner = <Chart spec={block.chart} rows={ctx.allRows} color={ctx.tokens["color.brand.primary"] ?? "#F04E37"} />;
      break;
    case "image":
      inner = val ? <img src={String(val)} alt="" loading="lazy" /> : <span class="ph">no image</span>;
      break;
    case "button": {
      const action = ctx.app.actions?.find((a) => a.id === block.actionId);
      inner = (
        <button
          type="button"
          data-action={block.actionId ?? ""}
          onClick={(e) => {
            e.stopPropagation(); // カードの行タップ（onSelectRow）を巻き込まない
            if (block.actionId) ctx.onAction?.(block.actionId, row);
          }}
        >
          {label(block.content ?? action?.name, ctx.locale)}
        </button>
      );
      break;
    }
    case "divider":
      inner = <hr />;
      break;
    case "spacer":
      inner = null;
      break;
    case "container": {
      if (depth >= MAX_DEPTH) return null; // 入れ子上限超過はスキップ
      const sub = block.position.colSpan;
      inner = (
        <div class="grid sub" style={`grid-template-columns:repeat(${sub},1fr)`}>
          {(block.children ?? []).map((c) => (
            <Block key={c.id} block={c} ctx={ctx} row={row} depth={depth + 1} gridColumns={sub} />
          ))}
        </div>
      );
      break;
    }
    case "ref-list":
      inner = <span class="ph">[ref-list: {block.binding?.columnId ?? ""}]</span>;
      break;
    default:
      return null; // 未知 component（custom 等）は安全にスキップ
  }

  const s = styleCss(block.style, ctx.tokens);
  const style = s ? `${gridCss};${s}` : gridCss;
  return (
    <div class={`blk blk-${block.component}`} style={style} data-blk={block.id}>
      {inner}
    </div>
  );
}
